import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';

import * as history from '@shared/router/location';
import { useCurrentRoutePath } from '@shared/router/state';
import * as uri from '@shared/router/uri';

export type LinkNavigation = 'push' | 'replace';

const DefaultLinkNavigation: LinkNavigation = 'push' as const;

const DefaultLinkTransition: history.NavigationTransition = 'default' as const;

const isOpenInNewTabEvent = (event: MouseEvent) =>
	event.ctrlKey || event.shiftKey || event.metaKey || event.button === 1;

interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
	href?: string;
	back?: boolean;
	navigation?: LinkNavigation;
	transition?: history.NavigationTransition;

	children: ReactNode;
	className?: string;

	stopPropagation?: boolean;
}

export default function Link({
	href = '#',
	back,
	navigation = DefaultLinkNavigation,
	transition = DefaultLinkTransition,

	children,
	className,
	onClick,
	stopPropagation,

	...remainingAttributes
}: LinkProps): ReactNode {
	const currentPath = useCurrentRoutePath();
	const shouldNavigate = uri.isLocalPathOrUrl(href);

	const isDownload =
		remainingAttributes.download !== undefined &&
		remainingAttributes.download !== false;

	const navigate = (event: MouseEvent<HTMLAnchorElement>): void => {
		if (onClick) onClick(event);

		if (event.defaultPrevented) return;

		if (isOpenInNewTabEvent(event)) return;

		// @NOTE Let the browser handle download links natively
		if (isDownload) return;

		if (stopPropagation) {
			event.stopPropagation();
		}

		if (shouldNavigate) {
			event.preventDefault();

			if (!href || href.startsWith('#')) {
				return;
			}

			if (history.isCurrent(href)) {
				return;
			}

			if (back) {
				history.pop();
			} else if (navigation === 'replace') {
				history.replace(href, { transition });
			} else {
				history.push(href, { transition });
			}
		}
	};

	let props: any = {
		href,
		className,
		onClick: navigate,
	};

	if (!shouldNavigate) {
		props = {
			...props,
			rel: 'noreferrer noopener',
			target: '_blank',
			onClick: undefined,
		};
	} else {
		// Strip away domain from local urls
		if (href.match(/https?:/i)) {
			const nextUrl = new URL(href || '');
			const domainName = `${nextUrl.protocol}//${nextUrl.host}`;

			props.href = href.replace(domainName, '');
		}
	}

	if (currentPath.startsWith(href)) {
		props['data-current-tree'] = true;
	}

	const hrefSegments = href.split('/').filter(Boolean);
	if (
		hrefSegments.length >= 2 &&
		(currentPath === href || currentPath.startsWith(href + '/'))
	) {
		props['data-current-branch'] = true;
	}

	if (history.isCurrent(href)) {
		props['aria-current'] = 'page';
		props['data-current-page'] = true;
	}

	return (
		<a {...remainingAttributes} {...props}>
			{children}
		</a>
	);
}
